import { Box, Button, Flex, Heading, HStack, Spinner, Stack, Text } from '@chakra-ui/react';
import { ChevronLeftIcon, LockIcon } from '@chakra-ui/icons';
import { isNewCheckoutEnabled } from './v2/flag';
import { useUserContext } from 'providers/useUserContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MindbodyContract } from 'types/contract';
import { GiftCard } from 'types/giftCard';
import { Service } from 'types/service';
import { ContractCheckout } from './ContractCheckout';
import { GiftCardCheckout } from './GiftCardCheckout';
import { ServiceCheckout } from './ServiceCheckout';
import { useEffect, useState } from 'react';
import { useListContracts, useListServices } from 'hooks/useApiQueries';
import { useQuery } from '@tanstack/react-query';
import { HTTP } from 'utils/http';
import { withAdminNonce } from 'screens/Landing';
import { Navigate } from 'react-router-dom';
import { safeLocalStorage } from 'utils/safeStorage';

export interface GiftCardProduct extends GiftCard {
  type: 'giftcard';
  IsIntroOffer?: boolean;
}

export interface ServiceProduct extends Service {
  type: 'service';
  IsIntroOffer: boolean;
  /** Vendu par une landing événement (pricing option parfois hors vente en ligne). */
  fromLanding?: boolean;
}

export interface ContractProduct extends MindbodyContract {
  type: 'contract';
}

export type Product = ServiceProduct | GiftCardProduct | ContractProduct;

export function Checkout() {
  const { isLoggedIn } = useUserContext();
  // isCheckingAlmaPayment removed - handled in App.tsx now
  const { data: services, isLoading: isLoadingServices } = useListServices();
  const { data: contracts, isLoading: isLoadingContracts } = useListContracts();
  const navigate = useNavigate();
  const location = useLocation();
  const { productId, contractId } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [productNotFound, setProductNotFound] = useState(false);

  // Un produit absent du catalogue « vente en ligne » peut être vendu par une
  // landing événement : le serveur ne le sert que s'il figure dans une landing
  // publiée.
  const inCatalog = productId && services ? services.some((service) => service.Id === productId) : false;
  const needsLandingLookup = !!productId && !!services && !isLoadingServices && !inCatalog;
  const landingServiceQuery = useQuery({
    queryKey: ['/landing/service', productId],
    queryFn: () => withAdminNonce(HTTP.get<Service>(`/landing/service/${productId}`)).send(),
    enabled: needsLandingLookup,
    retry: false,
  });


  // Note: Le traitement Alma est maintenant géré au niveau App.tsx pour une meilleure performance

  // Récupérer le produit depuis l'URL si présent
  useEffect(() => {
    if (contractId && contracts && !isLoadingContracts) {
      const foundContract = contracts.find((c) => c.Id === Number(contractId));
      if (foundContract) {
        setProduct({ ...foundContract, type: 'contract' });
        setProductNotFound(false);
      } else {
        setProductNotFound(true);
      }
      return;
    }
    if (productId && services && !isLoadingServices) {
      const foundProduct = services.find((service) => service.Id === productId);
      if (foundProduct) {
        // IsIntroOffer : propager la vraie valeur renvoyée par /sale/services
        // (l'écraser à false faussait offer_type en GA4 et le garde-fou
        // anti re-achat d'offre de bienvenue via Alma).
        setProduct({ ...foundProduct, type: 'service', IsIntroOffer: foundProduct.IsIntroOffer ?? false });
        setProductNotFound(false);
      } else if (landingServiceQuery.data) {
        setProduct({ ...landingServiceQuery.data, type: 'service', IsIntroOffer: false, fromLanding: true });
        setProductNotFound(false);
      } else if (landingServiceQuery.isError) {
        // Ni dans le catalogue, ni dans une landing publiée
        setProductNotFound(true);
      }
    }
  }, [
    productId,
    contractId,
    services,
    contracts,
    isLoadingServices,
    isLoadingContracts,
    landingServiceQuery.data,
    landingServiceQuery.isError,
  ]);

  // Alma payment processing UI removed - handled by overlay in App.tsx

  // Si on a un productId mais que les services sont encore en cours de chargement, attendre
  if (productId && (isLoadingServices || (needsLandingLookup && landingServiceQuery.isPending))) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minH="50vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  // Idem pour les contracts (route /checkout/contract/:id)
  if (contractId && isLoadingContracts) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minH="50vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  // Si le produit n'a pas été trouvé dans la liste des services après chargement complet, rediriger
  if (productNotFound) {
    return <Navigate to="/tarifs" replace />;
  }

  // Utiliser le produit de l'URL ou celui du state de location
  const checkoutProduct = product || location.state?.product;

  // Vérifier si un paiement Alma est en cours (présence de pid dans l'URL ou localStorage)
  const params = new URLSearchParams(location.search);
  let almaPaymentId = params.get('pid');

  // Vérifier aussi le localStorage
  if (!almaPaymentId) {
    const storedPid = safeLocalStorage.getItem('alma_pending_payment');
    if (storedPid) {
      almaPaymentId = storedPid;
    }
  }

  // Ne pas rediriger si un paiement Alma est en cours de traitement
  if (almaPaymentId) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minH="50vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  // Seulement rediriger vers tarifs si on n'a pas de productId/contractId ET pas de checkoutProduct
  if (!productId && !contractId && !checkoutProduct) {
    return <Navigate to="/tarifs" replace />;
  }

  if (!isLoggedIn) {
    // Naviguer vers la page de login avec l'URL actuelle comme onLoginRedirect
    const currentPath = location.pathname + location.search + location.hash;

    navigate('/account', {
      state: {
        onLoginRedirect: currentPath,
        product: checkoutProduct,
      },
      replace: true,
    });
    return null;
  }

  // Nouveau Panier (nouvelle interface) : en-tête discret (retour + paiement sécurisé),
  // le fil d'Ariane et la mise en page vivent dans CheckoutFormV2. Le bas de
  // page est réservé sur mobile pour la barre de paiement fixe.
  const isV2 = isNewCheckoutEnabled();

  return (
    <Stack
      className={isV2 ? 'CheckoutSummary CheckoutV2Page' : 'CheckoutSummary'}
      w="100%"
      maxW="1200px"
      mx="auto"
      px={{ base: 3, md: 6, lg: 8 }}
      pb={isV2 ? { base: '140px', md: 10 } : undefined}
    >
      {isV2 ? (
        <Flex justify="space-between" align="center" my={{ base: 3, md: 5 }} className="CheckoutV2 cv2 cv2-topbar CheckoutV2Header">
          <Button variant="unstyled" display="inline-flex" alignItems="center" gap={1} onClick={() => navigate(-1)}>
            <ChevronLeftIcon boxSize="18px" /> Retour
          </Button>
          <HStack spacing={1.5}>
            <LockIcon boxSize="10px" color="#7a7a7a" />
            <Text>Paiement sécurisé</Text>
          </HStack>
        </Flex>
      ) : (
        <Stack
          spacing={4}
          width="100%"
          margin="1em 0 2em"
          direction={{ base: 'column-reverse', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ base: 'center', md: 'flex-start' }}
        >
          <Heading
            className="accountMainTitle"
            size="md"
            textTransform="uppercase"
            textAlign={{ base: 'center', md: 'left' }}
          >
            Récapitulatif de commande
          </Heading>
          <Button variant="contained" alignSelf={{ base: 'flex-end', md: 'center' }} onClick={() => navigate(-1)}>
            Retour
          </Button>
        </Stack>
      )}

      {checkoutProduct?.type === 'giftcard' ? (
        <Box className="GiftCardCheckout">
          <GiftCardCheckout product={checkoutProduct} />
        </Box>
      ) : checkoutProduct?.type === 'contract' ? (
        <Box className="ContractCheckout">
          <ContractCheckout contract={checkoutProduct} />
        </Box>
      ) : checkoutProduct ? (
        <Box className="ServiceCheckout">
          <ServiceCheckout product={checkoutProduct} />
        </Box>
      ) : (
        <Box display="flex" alignItems="center" justifyContent="center" minH="50vh">
          <Spinner size="xl" />
        </Box>
      )}
    </Stack>
  );
}
